import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { saveAs } from 'file-saver';

import type {
  AppfolioConfig,
  RentManagerConfig,
  ResmanConfig,
  StructureBilling,
  UnitBilling,
} from '@/Structures/types';
import type { RmChargePayload } from '@/Structures/View/components/pmsApi';
import { convertArrayToCsvString, naturalCompare } from '../util';

dayjs.extend(utc);
dayjs.extend(timezone);

export type PmsExportRow = {
  'Property Short Name': string;
  'Unit Name': string;
  Amount: string;
  Date: string;
  'Charge Type': string;
  Comment: string;
  Reference: string;
};

export type MidCycleWarning = {
  unitName: string;
  invoiceCount: number;
};

/**
 * Detect units with mid-cycle tenant changes (multiple invoices for one unit).
 */
export function detectMidCycleChanges(units: UnitBilling[], invoices: any[]): MidCycleWarning[] {
  const warnings: MidCycleWarning[] = [];
  for (const unit of units) {
    const unitInvoices = invoices.filter((inv: any) => inv.unitId === unit.id);
    if (unitInvoices.length > 1) {
      warnings.push({
        unitName: unit.name,
        invoiceCount: unitInvoices.length,
      });
    }
  }
  return warnings;
}

/** Standard PMS-export billable check: skip non-billable, COMMON_AREA, NON_BILLABLE. */
function isStandardBillable(unit: UnitBilling): boolean {
  const isBillable = unit.is_billable ?? true;
  const flags = unit.unit_flags ?? [];
  return isBillable && !flags.includes('COMMON_AREA') && !flags.includes('NON_BILLABLE');
}

/** Options for `buildPmsExportRows`. All providers share the standard billable filter
 * and a natural-compare sort; providers customize the row shape via `mapRow` and may
 * add provider-specific filters (e.g. AppFolio skips units without a mapped Unit ID). */
export interface BuildPmsExportRowsOptions<TRow> {
  enrichedUnits: UnitBilling[];
  /** Optional additional filter applied after the standard billable check. */
  extraFilter?: (unit: UnitBilling) => boolean;
  /** Sort key for a unit; defaults to `unit.name`. PMS providers typically use the
   * mapped PMS unit name so the CSV row order matches the customer's PMS UI. */
  unitSortKey?: (unit: UnitBilling) => string;
  /** Build the provider-specific row from a unit. */
  mapRow: (unit: UnitBilling) => TRow;
}

/**
 * Shared template for every PMS CSV writer: standard billable filter + optional
 * provider-specific filter, natural-compare sort, then provider-specific row build.
 * One place to change cross-provider behavior (e.g. add a new flag exclusion, change
 * the sort algorithm); all writers pick it up automatically.
 */
export function buildPmsExportRows<TRow>(opts: BuildPmsExportRowsOptions<TRow>): TRow[] {
  const { enrichedUnits, extraFilter, unitSortKey, mapRow } = opts;
  const sortKey = unitSortKey ?? ((u: UnitBilling) => u.name);
  return enrichedUnits
    .filter((unit) => isStandardBillable(unit) && (extraFilter ? extraFilter(unit) : true))
    .sort((a, b) => naturalCompare(sortKey(a), sortKey(b)))
    .map(mapRow);
}

/**
 * Build Rent Manager export rows from enriched billing data.
 * Only includes billable units.
 *
 * @param unitPmsNames - Map of unit ID → PMS unit name (from units.pms_unit_name column)
 */
export function buildRentManagerExportRows(
  bill: StructureBilling,
  enrichedUnits: UnitBilling[],
  pmsConfig: RentManagerConfig,
  tz: string,
  unitPmsNames: Record<string, string>,
): PmsExportRow[] {
  const formattedDate = dayjs.unix(bill.end_time).tz(tz).format('MM/DD/YYYY');
  const startDate = dayjs.unix(bill.start_time).tz(tz).format('MM/DD/YYYY');
  const endDate = formattedDate;

  return buildPmsExportRows<PmsExportRow>({
    enrichedUnits,
    unitSortKey: (u) => unitPmsNames[u.id] || u.name,
    mapRow: (unit) => {
      // Total charge = water usage cost + allocated fees
      const amount = (unit.unit_cost_meter ?? 0) + (unit.unit_cost ?? 0);
      const unitName = unitPmsNames[unit.id] || unit.name;
      return {
        'Property Short Name': pmsConfig.property_short_name,
        'Unit Name': unitName,
        Amount: amount.toFixed(2),
        Date: formattedDate,
        'Charge Type': pmsConfig.default_charge_type || '',
        Comment: `Water charges ${startDate} - ${endDate}`,
        Reference: bill.billing_id,
      };
    },
  });
}

/**
 * Generate and download a Rent Manager CSV export.
 *
 * @param unitPmsNames - Map of unit ID → PMS unit name (from units.pms_unit_name column)
 */
export async function downloadRentManagerCsv(
  bill: StructureBilling,
  enrichedUnits: UnitBilling[],
  pmsConfig: RentManagerConfig,
  tz: string,
  structureName: string,
  unitPmsNames: Record<string, string>,
): Promise<void> {
  const rows = buildRentManagerExportRows(bill, enrichedUnits, pmsConfig, tz, unitPmsNames);

  if (rows.length === 0) {
    throw new Error('No billable units to export');
  }

  const csvString = await convertArrayToCsvString(rows);
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });

  const startDate = dayjs.unix(bill.start_time).tz(tz).format('YYYY-MM-DD');
  const endDate = dayjs.unix(bill.end_time).tz(tz).format('YYYY-MM-DD');
  const safeName = structureName.replace(/[^a-zA-Z0-9]/g, '_');
  const filename = `RM_Export_${safeName}_${startDate}_${endDate}.csv`;

  saveAs(blob, filename);
}

/**
 * Build Rent Manager charge payloads for API upload.
 * Same filtering as CSV export, but additionally filters out units without RM unit_id mapping.
 * Returns RmChargePayload[] for POST /Charges.
 *
 * @param unitPmsIds - Map of unit ID → RM UnitID integer
 */
export function buildRentManagerChargePayloads(
  bill: StructureBilling,
  enrichedUnits: UnitBilling[],
  pmsConfig: RentManagerConfig,
  tz: string,
  unitPmsIds: Record<string, number>,
): RmChargePayload[] {
  const transactionDate = dayjs.unix(bill.end_time).tz(tz).format('YYYY-MM-DD');
  const startDate = dayjs.unix(bill.start_time).tz(tz).format('MM/DD/YYYY');
  const endDate = dayjs.unix(bill.end_time).tz(tz).format('MM/DD/YYYY');

  return enrichedUnits
    .filter((unit) => {
      const isBillable = unit.is_billable ?? true;
      const flags = unit.unit_flags ?? [];
      return (
        isBillable &&
        !flags.includes('COMMON_AREA') &&
        !flags.includes('NON_BILLABLE') &&
        unitPmsIds[unit.id] != null
      );
    })
    .map((unit) => {
      const amount = (unit.unit_cost_meter ?? 0) + (unit.unit_cost ?? 0);
      return {
        Amount: parseFloat(amount.toFixed(2)),
        PropertyID: pmsConfig.property_id!,
        UnitID: unitPmsIds[unit.id],
        ChargeTypeID: pmsConfig.charge_type_id!,
        TransactionDate: transactionDate,
        Comment: `Water charges ${startDate} - ${endDate}`,
        Reference: bill.billing_id,
      };
    });
}

// --- AppFolio ---

/**
 * AppFolio's "Bulk Charges and Credits" import template (downloaded from inside AppFolio
 * via Bulk Charges and Credits, Format and Download Template section). Columns are in the
 * exact order AppFolio generates them. We populate Property Name, Unit ID, Unit Name,
 * Amount, and Description; Occupancy UID, Tenant Name, and Occupancy ID are left blank
 * because we don't capture them in SimpleSub's pms_config today. AppFolio appears to match
 * incoming charges to the current occupancy on Unit ID, so blanks should be tolerated; if
 * a customer's import rejects, we'll need to widen the schema to persist occupancy info.
 */
export type AppfolioExportRow = {
  'Property Name': string;
  'Unit ID': string;
  'Unit Name': string;
  'Occupancy UID': string;
  'Tenant Name': string;
  'Occupancy ID': string;
  Amount: string;
  Description: string;
};

/**
 * Build AppFolio export rows. AppFolio matches on Unit ID, so units without
 * a mapped unit_id are skipped (different from RM which falls back to unit name).
 * The four occupancy maps (Occupancy UID, Occupancy ID, Tenant Name) are populated
 * from each unit's persisted pms_config (captured when the customer uploaded the
 * AppFolio template at matching time). Empty maps cause those columns to come out
 * blank in the CSV.
 */
export function buildAppfolioExportRows(
  bill: StructureBilling,
  enrichedUnits: UnitBilling[],
  pmsConfig: AppfolioConfig,
  tz: string,
  unitPmsIds: Record<string, string>,
  unitPmsNames: Record<string, string>,
  unitOccupancyUids: Record<string, string> = {},
  unitOccupancyIds: Record<string, string> = {},
  unitTenantNames: Record<string, string> = {},
): AppfolioExportRow[] {
  const startDate = dayjs.unix(bill.start_time).tz(tz).format('MM/DD/YYYY');
  const endDate = dayjs.unix(bill.end_time).tz(tz).format('MM/DD/YYYY');

  return buildPmsExportRows<AppfolioExportRow>({
    enrichedUnits,
    // AppFolio matches on Unit ID; rows without one have no ledger to post to.
    extraFilter: (u) => unitPmsIds[u.id] != null,
    unitSortKey: (u) => unitPmsNames[u.id] || u.name,
    mapRow: (unit) => {
      const amount = (unit.unit_cost_meter ?? 0) + (unit.unit_cost ?? 0);
      return {
        'Property Name': pmsConfig.property_name,
        'Unit ID': unitPmsIds[unit.id],
        'Unit Name': unitPmsNames[unit.id] || unit.name,
        'Occupancy UID': unitOccupancyUids[unit.id] ?? '',
        'Tenant Name': unitTenantNames[unit.id] ?? '',
        'Occupancy ID': unitOccupancyIds[unit.id] ?? '',
        Amount: amount.toFixed(2),
        Description: `Water charges ${startDate} - ${endDate}`,
      };
    },
  });
}

export async function downloadAppfolioCsv(
  bill: StructureBilling,
  enrichedUnits: UnitBilling[],
  pmsConfig: AppfolioConfig,
  tz: string,
  structureName: string,
  unitPmsIds: Record<string, string>,
  unitPmsNames: Record<string, string>,
  unitOccupancyUids: Record<string, string> = {},
  unitOccupancyIds: Record<string, string> = {},
  unitTenantNames: Record<string, string> = {},
): Promise<void> {
  const rows = buildAppfolioExportRows(
    bill,
    enrichedUnits,
    pmsConfig,
    tz,
    unitPmsIds,
    unitPmsNames,
    unitOccupancyUids,
    unitOccupancyIds,
    unitTenantNames,
  );
  if (rows.length === 0) {
    throw new Error('No billable units to export');
  }
  const csvString = await convertArrayToCsvString(rows);
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
  const startDate = dayjs.unix(bill.start_time).tz(tz).format('YYYY-MM-DD');
  const endDate = dayjs.unix(bill.end_time).tz(tz).format('YYYY-MM-DD');
  const safeName = structureName.replace(/[^a-zA-Z0-9]/g, '_');
  const filename = `AppFolio_Export_${safeName}_${startDate}_${endDate}.csv`;
  saveAs(blob, filename);
}

// --- ResMan ---

export type ResmanExportRow = {
  Unit: string;
  'Resident Name': string;
  Date: string;
  'Transaction Category': string;
  'Payment Type': string;
  Reference: string;
  Description: string;
  Amount: string;
  Notes: string;
  'Split Ledger (Abbreviation)': string;
};

export function buildResmanExportRows(
  bill: StructureBilling,
  enrichedUnits: UnitBilling[],
  pmsConfig: ResmanConfig,
  tz: string,
  unitPmsNames: Record<string, string>,
  unitResidentNames: Record<string, string> = {},
): ResmanExportRow[] {
  const formattedDate = dayjs.unix(bill.end_time).tz(tz).format('MM/DD/YYYY');
  const startDate = dayjs.unix(bill.start_time).tz(tz).format('MM/DD/YYYY');
  const endDate = formattedDate;

  return buildPmsExportRows<ResmanExportRow>({
    enrichedUnits,
    unitSortKey: (u) => unitPmsNames[u.id] || u.name,
    mapRow: (unit) => {
      const amount = (unit.unit_cost_meter ?? 0) + (unit.unit_cost ?? 0);
      return {
        Unit: unitPmsNames[unit.id] || unit.name,
        'Resident Name': unitResidentNames[unit.id] ?? '',
        Date: formattedDate,
        'Transaction Category': pmsConfig.default_transaction_category ?? '',
        'Payment Type': pmsConfig.default_payment_type ?? '',
        Reference: bill.billing_id,
        Description: `Water charges ${startDate} - ${endDate}`,
        Amount: amount.toFixed(2),
        Notes: '',
        'Split Ledger (Abbreviation)': pmsConfig.default_split_ledger ?? '',
      };
    },
  });
}

export async function downloadResmanCsv(
  bill: StructureBilling,
  enrichedUnits: UnitBilling[],
  pmsConfig: ResmanConfig,
  tz: string,
  structureName: string,
  unitPmsNames: Record<string, string>,
  unitResidentNames: Record<string, string> = {},
): Promise<void> {
  const rows = buildResmanExportRows(
    bill,
    enrichedUnits,
    pmsConfig,
    tz,
    unitPmsNames,
    unitResidentNames,
  );
  if (rows.length === 0) {
    throw new Error('No billable units to export');
  }
  const csvString = await convertArrayToCsvString(rows);
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
  const startDate = dayjs.unix(bill.start_time).tz(tz).format('YYYY-MM-DD');
  const endDate = dayjs.unix(bill.end_time).tz(tz).format('YYYY-MM-DD');
  const safeName = structureName.replace(/[^a-zA-Z0-9]/g, '_');
  const filename = `ResMan_Export_${safeName}_${startDate}_${endDate}.csv`;
  saveAs(blob, filename);
}
